import * as React from 'react';
import {
  AlignItems,
  Button,
  classNames,
  Display,
  FlexDirection,
  FontSize,
  Heading,
  HeadingLevel,
  Icon,
  IconColor,
  IconName,
  IconSize,
  Input,
  Link,
  ListStyleType,
  Margin,
  MinWidth,
  Padding,
  RadioButton,
  RadioButtonGroup,
  TextAlign,
} from '@snoam/pinata';
import { convertNodeToElement } from 'react-html-parser';
import generatePropsFromAttributes from 'react-html-parser/src/utils/generatePropsFromAttributes';

const defaultNodeClassNames = {
  figure: classNames(Padding.PY_0, Padding.PX_4),
  form: classNames(Padding.P_2),
  h1: classNames(Margin.MB_4),
  h2: classNames(Margin.MB_4),
  h3: classNames(Margin.MB_4),
  h4: classNames(Margin.MB_3),
  h5: classNames(Margin.MB_3),
  h6: classNames(Margin.MB_3),
  img: classNames(Padding.PY_2),
  li: classNames(
    Display.FLEX,
    Padding.PT_2,
    FlexDirection.FLEX_ROW,
    AlignItems.ITEMS_CENTER,
    FontSize.TEXT_BASE,
  ),
  p: classNames(Margin.MY_4),
  radiogroup: classNames(TextAlign.TEXT_LEFT),
  ul: classNames(ListStyleType.LIST_NONE, Margin.MY_2),
};

const supportedNodeTypes = [
  'a',
  'p',
  'h1',
  'h2',
  'h3',
  'h4',
  'ul',
  'li',
  'form',
  'input',
  'radiogroup',
  'span',
  'b',
  'strong',
  'i',
  'em',
  'img',
];

type NodeClasses = typeof defaultNodeClassNames;

export const transformHtmlFactory = (
  nodeClassNames: Partial<NodeClasses> = {},
  stripAllHtmlfromNode?: boolean,
) => {
  return function transformHtml(node, index, strip = false) {
    const stripAllHtml = stripAllHtmlfromNode || strip;
    const classNamesNodeMap = Object.assign(
      {},
      defaultNodeClassNames,
      nodeClassNames,
    );
    const classes = Object.prototype.hasOwnProperty.call(
      classNamesNodeMap,
      node.name,
    )
      ? classNamesNodeMap[node.name]
      : '';

    if (node.type === 'tag' && stripAllHtml) {
      return node.children.map((child, i) => transformHtml(child, i));
    }

    if ((node.type === 'tag' && node.name === 'b') || node.name === 'strong') {
      const props = generatePropsFromAttributes(node.attribs, index);
      return (
        <strong {...props}>
          {node.children.map((child, i) => transformHtml(child, i))}
        </strong>
      );
    }

    if ((node.type === 'tag' && node.name === 'i') || node.name === 'em') {
      const props = generatePropsFromAttributes(node.attribs, index);
      return (
        <em {...props}>
          {node.children.map((child, i) => transformHtml(child, i))}
        </em>
      );
    }

    if (node.type === 'tag' && node.name === 'a') {
      const props = generatePropsFromAttributes(node.attribs, index);
      return (
        <Link {...props} target={'_blank'} rel="noopener">
          {node.children.map((child, i) => transformHtml(child, i))}
        </Link>
      );
    }

    if (node.type === 'tag' && node.name === 'p') {
      const props = generatePropsFromAttributes(node.attribs, index);
      return (
        <p {...props} className={classes}>
          {node.children.map((child, i) => transformHtml(child, i))}
        </p>
      );
    }

    if (node.type === 'tag' && node.name === 'h1') {
      const props = generatePropsFromAttributes(node.attribs, index);
      return (
        <Heading level={HeadingLevel.ONE} {...props} className={classes}>
          {node.children.map((child, i) => transformHtml(child, i))}
        </Heading>
      );
    }

    if (node.type === 'tag' && node.name === 'h2') {
      const props = generatePropsFromAttributes(node.attribs, index);
      return (
        <Heading level={HeadingLevel.TWO} {...props} className={classes}>
          {node.children.map((child, i) => transformHtml(child, i))}
        </Heading>
      );
    }

    if (node.type === 'tag' && node.name === 'h3') {
      const props = generatePropsFromAttributes(node.attribs, index);
      return (
        <Heading
          key={`heading_${index}`}
          level={HeadingLevel.THREE}
          {...props}
          className={classes}
        >
          {node.children.map((child, i) => transformHtml(child, i))}
        </Heading>
      );
    }

    if (node.type === 'tag' && node.name === 'h4') {
      const props = generatePropsFromAttributes(node.attribs, index);
      return (
        <Heading
          key={`heading_${index}`}
          level={HeadingLevel.FOUR}
          {...props}
          className={classes}
        >
          {node.children.map((child, i) => transformHtml(child, i))}
        </Heading>
      );
    }

    if (node.type === 'tag' && node.name === 'h5') {
      const props = generatePropsFromAttributes(node.attribs, index);
      return (
        <Heading
          key={`heading_${index}`}
          level={HeadingLevel.FIVE}
          {...props}
          className={classes}
        >
          {node.children.map((child, i) => transformHtml(child, i))}
        </Heading>
      );
    }

    if (node.type === 'tag' && node.name === 'h6') {
      const props = generatePropsFromAttributes(node.attribs, index);
      return (
        <Heading
          key={`heading_${index}`}
          level={HeadingLevel.SIX}
          {...props}
          className={classes}
        >
          {node.children.map((child, i) => transformHtml(child, i))}
        </Heading>
      );
    }

    if (node.type === 'tag' && node.name === 'ul') {
      const props = generatePropsFromAttributes(node.attribs, index);
      return (
        <ul key={`ul_${index}`} {...props} className={classes}>
          {node.children.map((child, i) => transformHtml(child, i))}
        </ul>
      );
    }

    if (node.type === 'tag' && node.name === 'li') {
      const props = generatePropsFromAttributes(node.attribs, index);
      return (
        <li key={`li_${index}`} {...props} className={classes}>
          {
            <Icon
              name={IconName.CHECK}
              size={IconSize.BASE}
              color={IconColor.PRIMARY_1}
              className={classNames(Margin.MR_4, MinWidth.MIN_W_6)}
            />
          }
          {node.children.map((child, i) => transformHtml(child, i))}
        </li>
      );
    }

    if (node.type === 'tag' && node.name === 'form') {
      const props = generatePropsFromAttributes(node.attribs, index);
      return (
        <form {...props} className={classes}>
          {node.children.map((child, i) => transformHtml(child, i))}
        </form>
      );
    }

    if (node.type === 'tag' && node.name === 'input') {
      const props = generatePropsFromAttributes(node.attribs, index);
      if (props.type === 'hidden') {
        return <input {...props} />;
      }
      if (props.type === 'submit') {
        return <Button {...props} text={props.value} />;
      }
      if (props.type === 'radio') {
        return <RadioButton {...props} text={props.placeholder} />;
      }
      return <Input {...props} />;
    }

    if (node.name === 'radiogroup') {
      const props = generatePropsFromAttributes(node.attribs, index);
      return (
        <RadioButtonGroup name={props.name}>
          <div className={classes}>
            {node.children.map((child, i) => transformHtml(child, i))}
          </div>
        </RadioButtonGroup>
      );
    }

    if (node.type === 'tag' && node.name === 'span') {
      const props = generatePropsFromAttributes(node.attribs, index);
      return (
        <span {...props}>
          {node.children.map((child, i) => transformHtml(child, i))}
        </span>
      );
    }

    if (node.type === 'tag' && node.name === 'img') {
      const props = generatePropsFromAttributes(node.attribs, index);
      return <img {...props} style={{ display: 'none' }} />;
    }

    if (node.type === 'tag' && !supportedNodeTypes.includes(node.name)) {
      return node.children.map((child, i) => transformHtml(child, i, true));
    }

    return convertNodeToElement(node, index, transformHtml);
  };
};
